import gql from "graphql-tag";
import { platform } from "~/platform/socket";
import { type ModifiedRecord, recordCacheStore, setRecordCacheStore } from "./store";

/**
 * Save the value in the cache
 * @param id
 * @param record
 */
export function setRecordCache<T>(id: string, record: T): void {
	setRecordCacheStore("cache", id, record);
}

/**
 * Get the value from the cache
 * @param id
 */
export function getRecordCache<T>(id: string): T | undefined {
	return recordCacheStore.cache?.[id];
}

/**
 * Remove the value from the cache
 * @param id
 */
export function removeRecordCache(id: string): void {
	setRecordCacheStore("cache", (cache) => {
		delete cache[id];
	});
}

/**
 * Check if the cache has the record
 * @param id
 */
export function hasRecordCache(id: string): boolean {
	return id in recordCacheStore.cache;
}

/**
 * Clear the cache
 * @param key
 */
export function clearRecordCache(): void {
	setRecordCacheStore("cache", {});
}

/**
 * Validate the cache
 * @param key
 * @param record
 */
export async function validateRecordCache<T extends ModifiedRecord>(id: string, record?: T): Promise<boolean> {
	if (!record) {
		return false;
	}

	const query = gql`
		query ($id: ID!) {
			modifiedAt(id: $id)
		}
	`;

	const result = await platform.query<{ modifiedAt: string }>(query, { id });

	return result.modifiedAt === record.modifiedAt;
}

/**
 * Load the cache from local storage.
 */
export function loadRecordCache(): void {
	const cache = localStorage.getItem("record-cache");
	cache && deserializeRecordCache(cache);
}

/**
 * Save the cache to local storage.
 */
export function saveRecordCache(): void {
	localStorage.setItem("record-cache", serializeRecordCache());
}

/**
 * Serialize the cache
 * @returns
 */
export function serializeRecordCache(): string {
	return JSON.stringify(recordCacheStore.cache);
}

/**
 * Deserialize the cache
 * @param data
 */
export function deserializeRecordCache(data: string) {
	setRecordCacheStore("cache", JSON.parse(data));
}

/**
 * Handles a simple cache mechanism for record(s)
 * @param id
 * @param fetcher
 */
export async function cacheHandler<T extends ModifiedRecord>(id: string, fetcher: (id: string) => Promise<T>) {
	let obj: T | undefined;

	if (!hasRecordCache(id)) {
		obj = await fetcher(id);
		setRecordCache(id, obj);
	} else {
		obj = getRecordCache(id);
		const valid = await validateRecordCache(id, obj);

		if (!valid) {
			obj = await fetcher(id);
			setRecordCache(id, obj);
		}
	}

	return obj;
}
